import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { WorkspaceProject } from "@/hooks/useWorkspaceProjects";

const PROJECT_COLORS = [
  { name: "Azul", value: "bg-blue-500" },
  { name: "Verde", value: "bg-green-500" },
  { name: "Amarelo", value: "bg-yellow-500" },
  { name: "Laranja", value: "bg-orange-500" },
  { name: "Vermelho", value: "bg-red-500" },
  { name: "Roxo", value: "bg-purple-500" },
  { name: "Rosa", value: "bg-pink-500" },
  { name: "Ciano", value: "bg-cyan-500" },
  { name: "Cinza", value: "bg-gray-500" },
  { name: "Índigo", value: "bg-indigo-500" },
];

interface ProjectManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: WorkspaceProject[];
  onCreate: (input: { name: string; description?: string; color?: string }) => Promise<unknown>;
  onUpdate: (
    id: string,
    patch: Partial<Pick<WorkspaceProject, "name" | "description" | "color">>,
  ) => Promise<boolean>;
  onArchive: (id: string) => Promise<boolean>;
}

export function ProjectManagerDialog({
  open,
  onOpenChange,
  projects,
  onCreate,
  onUpdate,
  onArchive,
}: ProjectManagerDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(PROJECT_COLORS[0].value);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate({ name: name.trim(), description: description.trim() || undefined, color });
      setName("");
      setDescription("");
      setColor(PROJECT_COLORS[0].value);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    const ok = await onUpdate(editingId, { name: editName.trim(), color: editColor });
    if (ok) {
      setEditingId(null);
      setEditName("");
      setEditColor("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Projetos</DialogTitle>
          <DialogDescription>
            Crie e gerencie projetos de tarefas. Cada projeto inicia sem colunas Kanban.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-3 rounded-lg border p-3">
            <Label>Novo projeto</Label>
            <Input
              placeholder="Nome do projeto"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              placeholder="Descrição (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all",
                    c.value,
                    color === c.value ? "ring-2 ring-offset-2 ring-primary" : "hover:scale-110",
                  )}
                  title={c.name}
                />
              ))}
            </div>
            <Button size="sm" onClick={() => void handleCreate()} disabled={submitting || !name.trim()}>
              <Plus className="w-4 h-4 mr-1" />
              Criar projeto
            </Button>
          </div>

          <div className="space-y-2">
            {projects.map((project) => (
              <div key={project.id} className="flex items-center gap-2 p-2 rounded-lg border">
                {editingId === project.id ? (
                  <div className="flex-1 space-y-2">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" />
                    <div className="flex flex-wrap gap-1">
                      {PROJECT_COLORS.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setEditColor(c.value)}
                          className={cn("w-5 h-5 rounded-full", c.value, editColor === c.value && "ring-2 ring-primary")}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => void handleSaveEdit()}>Salvar</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={cn("w-3 h-3 rounded-full shrink-0", project.color)} />
                    <span className="flex-1 truncate text-sm font-medium">{project.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingId(project.id);
                        setEditName(project.name);
                        setEditColor(project.color);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => void onArchive(project.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
